import { type Card, type Suit, fullDeck, sameCard, sortHand } from '../trick/cards.js';
import { seededRandom, shuffled } from '../trick/rng.js';
import { type TrickPlay, followSuit, trickWinner } from '../trick/rules.js';
import {
  BRIDGE_SEATS,
  type BridgeControllers,
  type BridgeSeat as Seat,
  type BridgeSide as Side,
  SINGLE_PLAYER,
  clockwiseAfter,
  leftOf,
  sideOf,
} from '../bridge/seats.js';

export type { Seat, Side };

export class IllegalWhistMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalWhistMoveError';
  }
}

export interface WhistCompletedTrick {
  readonly leader: Seat;
  readonly plays: readonly TrickPlay<Seat>[];
  readonly winner: Seat;
}

export type WhistPhase = 'play' | 'done';

/**
 * Classic whist: partners sit opposite (N-S against E-W), 13 cards each, no auction and no dummy.
 * Trump is the suit of the last card dealt, which is the dealer's and is shown to everyone. The state
 * holds all four hands; clients and robots read only `whistViewFor` (view.ts).
 */
export interface WhistGame {
  readonly seed: number;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
  readonly hands: Readonly<Record<Seat, readonly Card[]>>;
  /** The last card dealt (the dealer's). Public from the start. */
  readonly trumpCard: Card;
  readonly trump: Suit;
  readonly phase: WhistPhase;
  readonly turn: Seat;
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly WhistCompletedTrick[];
  readonly tricksWon: Readonly<Record<Side, number>>;
}

export interface NewWhistGameOptions {
  readonly seed: number;
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
}

/** Deal 13 cards each from a seeded shuffle, clockwise from the seat after the dealer; the last card is the dealer's and names trump. */
export function newWhistGame(opts: NewWhistGameOptions): WhistGame {
  const dealer = opts.dealer ?? 'N';
  const deck = shuffled(fullDeck(), seededRandom(opts.seed));
  const order = clockwiseAfter(dealer);
  const dealt: Record<Seat, Card[]> = { N: [], E: [], S: [], W: [] };
  deck.forEach((card, i) => dealt[order[i % 4] as Seat].push(card));
  const trumpCard = deck[deck.length - 1] as Card;
  const hands = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, sortHand(dealt[s])])) as Record<Seat, Card[]>;
  return {
    seed: opts.seed,
    dealer,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    hands,
    trumpCard,
    trump: trumpCard.suit,
    phase: 'play',
    turn: leftOf(dealer),
    trick: [],
    tricks: [],
    tricksWon: { NS: 0, EW: 0 },
  };
}

/** The cards the seat to play may play: follow suit if possible. */
export function whistLegalPlays(game: WhistGame): Card[] {
  if (game.phase !== 'play') return [];
  return followSuit(game.hands[game.turn], game.trick[0]?.card.suit);
}

export function whistPlayCard(game: WhistGame, card: Card): WhistGame {
  if (game.phase !== 'play') throw new IllegalWhistMoveError('the game is over');
  if (!whistLegalPlays(game).some((c) => sameCard(c, card))) {
    throw new IllegalWhistMoveError(`${game.turn} cannot play that card now`);
  }
  const seat = game.turn;
  const hands = { ...game.hands, [seat]: game.hands[seat].filter((c) => !sameCard(c, card)) };
  const trick = [...game.trick, { seat, card }];
  if (trick.length < 4) return { ...game, hands, trick, turn: leftOf(seat) };

  const winner = trickWinner(trick, game.trump);
  const tricks = [...game.tricks, { leader: (trick[0] as TrickPlay<Seat>).seat, plays: trick, winner }];
  const tricksWon = { ...game.tricksWon, [sideOf(winner)]: game.tricksWon[sideOf(winner)] + 1 };
  return { ...game, hands, trick: [], tricks, tricksWon, turn: winner, phase: tricks.length === 13 ? 'done' : 'play' };
}

export interface WhistResult {
  readonly winningSide: Side;
  readonly tricks: Readonly<Record<Side, number>>;
  /** Tricks above the first six (the "book"); 1..7. */
  readonly points: number;
}

/** Thirteen tricks cannot tie, so one side always has at least seven. */
export function whistResult(game: WhistGame): WhistResult | null {
  if (game.phase !== 'done') return null;
  const winningSide: Side = game.tricksWon.NS > game.tricksWon.EW ? 'NS' : 'EW';
  return { winningSide, tricks: game.tricksWon, points: game.tricksWon[winningSide] - 6 };
}
